if (!customElements.get('embroidery-group-order-form')) {
  // Declarative slide graph: a value is either the next slide id (linear step)
  // or a { key, cases } fork resolved against `this.selections[key]`.
  const SLIDE_GRAPH = {
    basic_info: 'quantity',
    quantity: { key: 'quantity_tier', cases: { 'Fewer than 30': 'spend', '30 or More': 'help' } },
    spend: { key: 'spend_threshold', cases: { No: 'standard_purchase', Yes: 'embroidery' } },
    help: {
      key: 'needs_help',
      cases: { "I'll place the order myself": 'embroidery', 'I need help': 'embroidery' },
    },
    embroidery: { key: 'needs_embroidery', cases: { No: 'guide', Yes: 'custom_logo' } },
    custom_logo: { key: 'custom_logo', cases: { Yes: 'upload', No: 'guide_preset' } },
  };

  class GroupOrderQuiz extends HTMLElement {
    connectedCallback() {
      this.digitizationId = this.dataset.digitizationId || null;
      this.apiBase = this.dataset.apiBase || 'https://winem.sparkyapps.dev';

      this.current = 'basic_info';
      this.history = [];

      this.slides = {};
      this.querySelectorAll('[data-slide]').forEach(el => {
        this.slides[el.dataset.slide] = el;
      });

      this.progressBar = this.querySelector('[data-progress-bar]');
      this.stepLabel = this.querySelector('[data-step-label]');
      this.backBtn = this.querySelector('[data-action="back"]');
      this.header = this.querySelector('[data-quiz-header]');

      // Upload state
      this.logo = null;
      this.uploadInput = this.querySelector('[data-upload-input]');
      this.uploader = this.querySelector('[data-uploader]');
      this.uploadLoader = this.querySelector('[data-upload-loader]');
      this.uploadEmpty = this.querySelector('[data-upload-empty]');
      this.uploadPreview = this.querySelector('[data-upload-preview]');
      this.uploadPreviewImg = this.querySelector('[data-upload-preview-img]');
      this.uploadPreviewName = this.querySelector('[data-upload-preview-name]');
      this.uploadError = this.querySelector('[data-upload-error]');
      this.submitBtn = this.querySelector('[data-slide="upload"] [data-submit-btn]');

      this.fields = {};
      this.querySelectorAll('[data-field]').forEach(el => {
        this.fields[el.dataset.field] = el;
      });

      this.selections = {};

      this.initEvents();
      this.updateProgress();
    }

    initEvents() {
      this.addEventListener('click', e => {
        const btn = e.target.closest('[data-action]');
        if (btn) {
          const action = btn.dataset.action;
          if (action === 'next') this.handleNext();
          else if (action === 'back') this.goBack();
          else if (action === 'trigger-upload') this.uploadInput?.click();
          else if (action === 'remove-upload') this.clearLogo();
          else if (action === 'submit') this.handleSubmit();
          else if (action === 'submit-no-embroidery') this.handleSubmitNoEmbroidery(btn);
        }

        // Option cards
        const optionBtn = e.target.closest('[data-option]');
        if (optionBtn) this.handleOptionSelect(optionBtn);

        // Collection checkboxes
        const checkCard = e.target.closest('[data-collection]');
        if (checkCard) this.handleCollectionToggle(checkCard);
      });

      if (this.uploadInput) {
        this.uploadInput.addEventListener('change', e => {
          const file = e.target.files[0];
          if (file) this.handleFile(file);
        });
      }

      if (this.uploader) {
        this.uploader.addEventListener('dragover', e => {
          e.preventDefault();
          this.uploader.classList.add('drag-over');
        });
        this.uploader.addEventListener('dragleave', () => {
          this.uploader.classList.remove('drag-over');
        });
        this.uploader.addEventListener('drop', e => {
          e.preventDefault();
          this.uploader.classList.remove('drag-over');
          const file = e.dataTransfer?.files[0];
          if (file) this.handleFile(file);
        });
      }
    }

    handleOptionSelect(btn) {
      const key = btn.dataset.option;
      const value = btn.dataset.value;
      this.selections[key] = value;
      this.querySelectorAll(`[data-option="${key}"]`).forEach(b => b.removeAttribute('data-selected'));
      btn.setAttribute('data-selected', '');
    }

    handleCollectionToggle(card) {
      if (card.hasAttribute('data-selected')) {
        card.removeAttribute('data-selected');
      } else {
        card.setAttribute('data-selected', '');
      }
      const selected = Array.from(this.querySelectorAll('[data-collection][data-selected]'))
        .map(c => c.dataset.collection);
      this.selections['collections'] = selected;

      if (card.dataset.collection === 'Other') {
        const otherInput = this.querySelector('[data-collections-other-input]');
        otherInput?.classList.toggle('hidden', !card.hasAttribute('data-selected'));
      }
    }

    handleNext() {
      const slideEl = this.slides[this.current];
      const errorEl = slideEl?.querySelector('[data-slide-error]');

      const err = this.validateSlide(this.current);
      if (err) {
        if (errorEl) { errorEl.textContent = err; errorEl.classList.remove('hidden'); }
        return;
      }
      if (errorEl) errorEl.classList.add('hidden');

      const next = this.getNextSlide();
      if (next) this.goTo(next);
    }

    getNextSlide() {
      const entry = SLIDE_GRAPH[this.current];
      if (!entry) return null;
      if (typeof entry === 'string') return entry;
      return entry.cases[this.selections[entry.key]] ?? null;
    }

    validateSlide(id) {
      const required = {
        basic_info: ['first_name', 'last_name', 'business_name', 'email', 'city'],
        quantity: [{ key: 'quantity_tier', type: 'option', label: 'Please choose an option.' }],
        spend: [{ key: 'spend_threshold', type: 'option', label: 'Please choose an option.' }],
        help: [{ key: 'needs_help', type: 'option', label: 'Please choose an option.' }],
        embroidery: [{ key: 'needs_embroidery', type: 'option', label: 'Please choose an option.' }],
        custom_logo: [{ key: 'custom_logo', type: 'option', label: 'Please choose an option.' }],
      };
      const rules = required[id];
      if (!rules) return null;

      for (const rule of rules) {
        if (typeof rule === 'string') {
          const val = this.fields[rule]?.value?.trim();
          if (!val) return 'Please fill in all required fields.';
          if (rule === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
            return 'Please enter a valid email address.';
          }
        } else if (rule.type === 'option') {
          if (!this.selections[rule.key]) return rule.label;
        }
      }
      return null;
    }

    showSlide(id) {
      const el = this.slides[id];
      if (!el) return;
      el.classList.remove('hidden');
      el.classList.add('flex');
    }

    hideSlide(id) {
      const el = this.slides[id];
      if (!el) return;
      el.classList.add('hidden');
      el.classList.remove('flex');
    }

    setBackVisible(visible) {
      if (visible) {
        this.backBtn?.classList.remove('hidden');
        this.backBtn?.classList.add('flex');
      } else {
        this.backBtn?.classList.add('hidden');
        this.backBtn?.classList.remove('flex');
      }
    }

    goTo(id) {
      this.history.push(this.current);
      this.hideSlide(this.current);
      this.current = id;
      this.showSlide(id);
      this.updateProgress();

      const isDone = id === 'done';
      this.header.classList.toggle('hidden', isDone);
      this.setBackVisible(this.history.length > 0 && !isDone);

      this.slides[id]?.querySelector('[data-field]')?.focus();
    }

    goBack() {
      if (!this.history.length) return;
      const prev = this.history.pop();
      this.hideSlide(this.current);
      this.current = prev;
      this.showSlide(prev);
      this.updateProgress();
      this.setBackVisible(this.history.length > 0);
    }

    updateProgress() {
      const step = this.history.length + 1;
      const total = step + this.remainingSteps(this.current);
      const pct = (step / total) * 100;
      if (this.progressBar) this.progressBar.style.width = `${pct}%`;
      if (this.stepLabel) this.stepLabel.textContent = `Step ${step} of ${total}`;
    }

    // Count of slides still ahead of `id`, following answers already given and
    // taking the longer branch of any not-yet-answered fork as a conservative
    // upper bound. Shrinks (never grows) as real answers replace estimates.
    remainingSteps(id) {
      const entry = SLIDE_GRAPH[id];
      if (!entry) return 0;
      if (typeof entry === 'string') return 1 + this.remainingSteps(entry);
      const answer = this.selections[entry.key];
      const target = answer != null ? entry.cases[answer] : undefined;
      if (target) return 1 + this.remainingSteps(target);
      const branches = Object.values(entry.cases);
      return 1 + Math.max(...branches.map(next => this.remainingSteps(next)));
    }

    // ── Upload ─────────────────────────────────────────────

    async handleFile(file) {
      const allowed = ['image/png', 'image/jpeg', 'image/svg+xml'];
      if (!allowed.includes(file.type)) {
        if (this.uploadInput) this.uploadInput.value = '';
        this.showUploadError('Please upload a PNG, JPG, or SVG file.');
        return;
      }
      this.clearUploadError();
      this.setUploadLoading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${this.apiBase}/api/upload`, { method: 'POST', body: fd });
        if (!res.ok) throw new Error('upload_failed');
        const data = await res.json();
        if (!data?.url) throw new Error('invalid_response');
        this.setLogo(data.url, file.name);
      } catch {
        if (this.uploadInput) this.uploadInput.value = '';
        this.showUploadError('Upload failed. Please try again.');
      } finally {
        this.setUploadLoading(false);
      }
    }

    setUploadLoading(loading) {
      this.uploader?.classList.toggle('loading', loading);
      if (loading) {
        this.uploadLoader?.classList.remove('hidden');
        this.uploadLoader?.classList.add('flex');
      } else {
        this.uploadLoader?.classList.add('hidden');
        this.uploadLoader?.classList.remove('flex');
      }
      if (this.uploadInput) this.uploadInput.disabled = loading;
    }

    setLogo(url, name) {
      this.logo = { id: crypto.randomUUID(), url, name };
      if (this.uploadPreviewImg) { this.uploadPreviewImg.src = url; this.uploadPreviewImg.alt = name; }
      if (this.uploadPreviewName) this.uploadPreviewName.textContent = name;
      this.uploadEmpty?.classList.add('hidden');
      this.uploadPreview?.classList.remove('hidden');
      this.uploadPreview?.classList.add('flex');
      if (this.submitBtn) this.submitBtn.disabled = false;
    }

    clearLogo() {
      this.logo = null;
      if (this.uploadInput) this.uploadInput.value = '';
      this.uploadPreview?.classList.add('hidden');
      this.uploadPreview?.classList.remove('flex');
      this.uploadEmpty?.classList.remove('hidden');
      if (this.submitBtn) this.submitBtn.disabled = true;
    }

    showUploadError(msg) {
      if (!this.uploadError) return;
      this.uploadError.textContent = msg;
      this.uploadError.classList.remove('hidden');
      this.uploader?.classList.add('error');
    }

    clearUploadError() {
      this.uploadError?.classList.add('hidden');
      this.uploader?.classList.remove('error');
    }

    // ── Submission ─────────────────────────────────────────

    // Derives the "how should this lead be served" outcome from the
    // quantity/spend/help answers, replacing what used to be a direct
    // Self Serve vs Customer Service question.
    deriveQualificationOutcome() {
      const { quantity_tier, spend_threshold, needs_help } = this.selections;
      if (quantity_tier === 'Fewer than 30') {
        return spend_threshold === 'Yes' ? 'Group Program Self-Serve' : 'Standard Website Purchase';
      }
      if (quantity_tier === '30 or More') {
        return needs_help === 'I need help' ? 'White-Glove Assisted Order Standard Pricing' : 'Group Program Self-Serve';
      }
      return '';
    }

    buildProperties() {
      const otherDetail = this.fields.collections_other?.value?.trim();
      const collections = Array.isArray(this.selections.collections)
        ? this.selections.collections.map(c => (c === 'Other' && otherDetail) ? `Other: ${otherDetail}` : c).join(', ')
        : '';
      return {
        _first_name:         this.fields.first_name?.value?.trim() || '',
        _last_name:          this.fields.last_name?.value?.trim() || '',
        _business:           this.fields.business_name?.value?.trim() || '',
        _location:           this.fields.city?.value?.trim() || '',
        _business_type:      this.fields.business_type?.value || '',
        _email:              this.fields.email?.value?.trim() || '',
        _collections:        collections,
        _quantity_tier:      this.selections.quantity_tier || '',
        _spend_threshold:    this.selections.spend_threshold || '',
        _needs_help:         this.selections.needs_help || '',
        _service_type:       this.deriveQualificationOutcome(),
        _needs_embroidery:   this.selections.needs_embroidery || '',
        _custom_logo:        this.selections.custom_logo || '',
      };
    }

    async handleSubmit() {
      if (!this.logo) {
        const errorEl = this.slides['upload']?.querySelector('[data-slide-error]');
        if (errorEl) { errorEl.textContent = 'Please upload your logo before submitting.'; errorEl.classList.remove('hidden'); }
        return;
      }
      if (!this.digitizationId) {
        console.warn('embroidery-group-order-form: no digitization variant ID configured');
        return;
      }

      const submitBtn = this.slides['upload']?.querySelector('[data-submit-btn]');
      this.setSubmitting(submitBtn, true);

      const bundleId = crypto.randomUUID();
      const props = {
        ...this.buildProperties(),
        _embroidery_bundle_id: bundleId,
        _embroidery_identifier: this.logo.id,
        _embroidery_logo_url: this.logo.url,
        _embroidery_bundle_component: 'digitization',
        _embroidery_source: 'group_order',
        _LPROP: 'Final Sale',
      };

      try {
        const metaobjectId = await this.submitToApi();

        const miniCart = document.querySelector('mini-cart');
        if (metaobjectId) {
          props._group_order_metaobject_id = metaobjectId;
        } else {
          console.warn('group-order: no metaobject id returned, property will not be set');
        }
        console.log('group-order cart properties:', props);
        const item = { id: Number(this.digitizationId), quantity: 1, properties: props };
        if (miniCart?.bulkAddToCart) {
          const res = await miniCart.bulkAddToCart([item]);
          if (res) { miniCart.updateCartHtml?.(res); miniCart.checkUbeInventory?.(); miniCart.open?.(); }
        } else {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [item] })
          });
          if (!res.ok) throw new Error('add_failed');
        }
        this.goTo('done');
      } catch {
        const errorEl = this.slides['upload']?.querySelector('[data-slide-error]');
        if (errorEl) { errorEl.textContent = 'Something went wrong. Please try again.'; errorEl.classList.remove('hidden'); }
      } finally {
        this.setSubmitting(submitBtn, false);
      }
    }

    async submitToApi() {
      const props = this.buildProperties();
      try {
        const response = await fetch(`${this.apiBase}/api/group-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name:         props._first_name,
            last_name:          props._last_name,
            business:           props._business,
            location:           props._location,
            business_type:      props._business_type,
            email:              props._email,
            collections:        props._collections,
            quantity_tier:      props._quantity_tier,
            spend_threshold:    props._spend_threshold,
            needs_help:         props._needs_help,
            service_type:       props._service_type,
            needs_embroidery:   props._needs_embroidery,
            custom_logo:        props._custom_logo,
            logo_url:           this.logo?.url || '',
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          console.warn('group-order API error:', response.status, data);
          return null;
        }
        console.log('group-order API metaobject id:', data?.id);
        return data?.id || null;
      } catch (err) {
        console.warn('group-order API:', err);
        return null;
      }
    }

    async handleSubmitNoEmbroidery(btn) {
      this.setSubmitting(btn, true);
      await this.submitToApi();
      this.setSubmitting(btn, false);
      this.goTo('done');
    }

    setSubmitting(btn, submitting) {
      if (!btn) return;
      btn.disabled = submitting;
      btn.querySelector('[data-submit-label]')?.classList.toggle('hidden', submitting);
      btn.querySelector('[data-submit-spinner]')?.classList.toggle('hidden', !submitting);
    }
  }

  customElements.define('embroidery-group-order-form', GroupOrderQuiz);
}
